import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MessageSquare, Zap, Users, Clock, ArrowRight, Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import ChatWidget from "@/components/ChatWidget";
import { TrustBadges } from "@/components/TrustBadges";
import Footer from "@/components/Footer";
import logoImage from "@/assets/iq-texting-logo.webp";
import { useNavigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { LoadingFallback } from "@/components/LoadingFallback";
import { RevenueLossCalculator } from "@/components/RevenueLossCalculator";
import { SITE_CONFIG, generateCanonicalUrl } from "@/lib/seoConfig";


// Lazy load heavy components that appear below the fold
const PricingPlans = lazy(() => import("@/components/PricingPlans").then(m => ({
  default: m.PricingPlans
})));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection").then(m => ({
  default: m.TestimonialsSection
})));
const CaseStudiesSection = lazy(() => import("@/components/CaseStudiesSection").then(m => ({
  default: m.CaseStudiesSection
})));
const Index = () => {
  const navigate = useNavigate();

  // Enhanced structured data for SEO with E-E-A-T signals
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [{
      "@type": "Organization",
      "name": "iQ Texting",
      "url": "https://iqtexting.com",
      "logo": logoImage,
      "description": "AI-powered website chat and SMS messaging platform that converts website visitors into customers",
      "foundingDate": "2024",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "US"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Customer Service",
        "availableLanguage": "English",
        "email": "support@iqtexting.com"
      }
    }, {
      "@type": "SoftwareApplication",
      "name": "iQ Texting",
      "applicationCategory": "BusinessApplication",
      "offers": {
        "@type": "AggregateOffer",
        "priceCurrency": "USD",
        "lowPrice": "0",
        "highPrice": "299"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "150"
      }
    }, {
      "@type": "FAQPage",
      "mainEntity": [{
        "@type": "Question",
        "name": "How does iQ Texting work with my existing website?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "iQ Texting integrates seamlessly with any website through a simple code snippet. Once installed, a chat widget appears on your site where visitors can enter their name, phone number, and message. When they submit, your team members receive the message as a text on their phones instantly."
        }
      }, {
        "@type": "Question",
        "name": "Can multiple team members receive messages from the same visitor?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! When a visitor submits a message through your website, you can configure up to 7 team members to receive the notification simultaneously via text message. The first person to respond takes ownership of that conversation."
        }
      }, {
        "@type": "Question",
        "name": "What happens when my team is unavailable or outside business hours?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "iQ Texting includes an intelligent AI assistant that automatically handles conversations during your off-hours. You can set your business hours in the dashboard, and when messages arrive outside those times, the AI responds based on your custom knowledge base and training."
        }
      }]
    }]
  };
  return <div className="min-h-screen bg-background">
      <Helmet>
        <title>{SITE_CONFIG.defaultTitle}</title>
        <meta name="description" content={SITE_CONFIG.defaultDescription} />
        <link rel="canonical" href={generateCanonicalUrl("/")} />
        <meta property="og:title" content={SITE_CONFIG.defaultTitle} />
        <meta property="og:description" content={SITE_CONFIG.defaultDescription} />
        <meta property="og:url" content={generateCanonicalUrl("/")} />
        <meta name="twitter:title" content={SITE_CONFIG.defaultTitle} />
        <meta name="twitter:description" content={SITE_CONFIG.defaultDescription} />
      </Helmet>
      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(structuredData)
    }} />
      {/* Skip to main content link for keyboard navigation */}
      <a href="#main" className="skip-to-main">
        Skip to main content
      </a>

      {/* Top Banner Headline */}
      <section className="bg-primary py-2">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl lg:text-5xl font-bold text-primary-foreground">
            iQ Texting: Convert 30-50% More Website Visitors with AI Chat & SMS
          </h1>
          <p className="text-2xl font-bold text-primary-foreground mt-2">
            Stop losing revenue to missed calls and slow replies.
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <header className="relative overflow-hidden bg-white" role="banner">
        {/* Background elements removed for white background */}
        
        <div className="relative container mx-auto px-4 pt-3 pb-4" id="main-content">
          {/* Logo at top center with Early Access badge - Optimized with responsive sizes */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4 mb-2">
            {/* Logo */}
            <img 
              src={logoImage} 
              alt="iQ Texting - AI-powered website chat and SMS messaging platform logo" 
              loading="eager"
              fetchPriority="high"
              width="480"
              height="138"
              className="w-auto h-16 md:h-20" 
            />
            
            {/* Early Access Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-200 shadow-sm">
              <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Early Access
              </span>
            </div>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-4 items-center">
            {/* Left Column - Problem Statement */}
            <div className="animate-fade-in">
              <div className="mb-2.5">
                <p className="text-xl font-bold mb-4 text-foreground">The Hidden Crisis:</p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <p className="text-lg">
                      <strong>62% of inquiries to small businesses go unanswered</strong> - costing $126,000/year on average
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <p className="text-lg">
                      <strong>85% of customers whose call you miss will NEVER call back</strong> - they're calling your competitor right now
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <p className="text-lg">
                      <strong>You have 5 minutes to respond</strong> - businesses that respond within 5 minutes are 100x more likely to convert
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <p className="text-lg">
                      <strong>First business to respond wins 78% of the time</strong> - your slow response is gifting sales to faster competitors
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Video Demo */}
            <div className="animate-slide-up">
              <Card className="p-8 border-2 bg-card">
                <div className="aspect-video bg-muted rounded-lg flex items-center justify-center mb-6" role="img" aria-label="Product demonstration video showing iQ Texting's real-time SMS automation, customer engagement features, and live chat interface in action">
                  <div className="text-center">
                    <p className="text-2xl font-bold mb-2">PRODUCT DEMO</p>
                    <p className="text-2xl font-bold">VIDEO</p>
                  </div>
                </div>
                <Button size="lg" className="w-full text-lg" onClick={() => navigate("/dashboard")}>
                  Get Started Today!
                </Button>
              </Card>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main id="main">
        {/* Revenue Loss Calculator Section */}
        <RevenueLossCalculator />


      {/* How It Works Section */}
      <section className="py-2.5 bg-background" aria-labelledby="how-it-works">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 id="how-it-works" className="text-3xl font-bold mb-4 text-primary">
              Here's How iQ Texting Flips the Script
            </h2>
            
            {/* Response time context */}
            <div className="bg-blue-50 p-6 rounded-lg mb-6 border-l-4 border-blue-600">
              <p className="text-xl font-bold text-blue-900 mb-2">
                The Research is Clear: You Have 5 Minutes to Respond or You Lose the Sale
              </p>
              <p className="text-lg text-gray-700">
                78% of customers buy from whoever responds FIRST. 
                Businesses that respond within 5 minutes are <strong>100x more likely to convert</strong>.
              </p>
              <p className="text-lg text-gray-700 mt-2">
                Here's how iQ Texting makes sure YOU'RE always first:
              </p>
            </div>
            
            <ul className="grid md:grid-cols-2 gap-x-8 gap-y-4 text-lg text-left max-w-4xl mx-auto">
              <li className="flex items-start gap-3">
                <span className="w-3 h-3 rounded-full bg-primary flex-shrink-0 mt-2" aria-hidden="true"></span>
                <span>Visitor sends a message → <strong>You're notified in under 30 seconds</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-3 h-3 rounded-full bg-primary flex-shrink-0 mt-2" aria-hidden="true"></span>
                <span>Up to 7 staff members notified at once</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-3 h-3 rounded-full bg-primary flex-shrink-0 mt-2" aria-hidden="true"></span>
                <span>Whoever replies first takes the conversation → <strong>Usually under 2 minutes</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-3 h-3 rounded-full bg-primary flex-shrink-0 mt-2" aria-hidden="true"></span>
                <span>Real conversations → <strong>Close deals while competitors check voicemail</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-3 h-3 rounded-full bg-primary flex-shrink-0 mt-2" aria-hidden="true"></span>
                <span>The visitor gets your reply right on their phone—just like texting a friend</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-3 h-3 rounded-full bg-primary flex-shrink-0 mt-2" aria-hidden="true"></span>
                <span>Have real instant conversations with potential customers</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-3 bg-muted/30" aria-labelledby="features">
        <div className="container mx-auto px-4">
          <div className="text-center mb-4">
            <h2 id="features" className="text-3xl lg:text-4xl font-bold mb-4">
              Key Features of iQ Texting
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Simple, powerful tools to capture and convert every visitor
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2">
            <article className="p-6 border-0 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-hover)] transition-shadow bg-[var(--gradient-card)] rounded-lg">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4" aria-hidden="true">
                <MessageSquare className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Website Chat Widget</h3>
              <p className="text-sm text-muted-foreground">
                Customizable text widget that captures visitor information and messages you instantly from your website
              </p>
            </article>

            <article className="p-6 border-0 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-hover)] transition-shadow bg-[var(--gradient-card)] rounded-lg">
              <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4" aria-hidden="true">
                <Users className="h-6 w-6 text-accent" />
              </div>
              <h3 className="font-semibold mb-2">Multi-Agent SMS Routing</h3>
              <p className="text-sm text-muted-foreground">
                Notify up to 7 team members simultaneously - first responder claims the conversation
              </p>
            </article>

            <article className="p-6 border-0 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-hover)] transition-shadow bg-[var(--gradient-card)] rounded-lg">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4" aria-hidden="true">
                <Clock className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">24/7 AI Assistant</h3>
              <p className="text-sm text-muted-foreground">
                Intelligent AI handles customer inquiries outside business hours with custom training
              </p>
            </article>

            <article className="p-6 border-0 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-hover)] transition-shadow bg-[var(--gradient-card)] rounded-lg">
              <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4" aria-hidden="true">
                <Zap className="h-6 w-6 text-accent" />
              </div>
              <h3 className="font-semibold mb-2">Real-Time Dashboard</h3>
              <p className="text-sm text-muted-foreground">
                Monitor conversations, track team performance, and analyze customer engagement metrics live
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Pricing Section - Lazy Loaded */}
      <section className="py-3 bg-background" aria-labelledby="pricing">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 id="pricing" className="text-3xl lg:text-4xl font-bold mb-4">
              Simple Pricing That Pays for Itself
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-2">
              Every plan includes rollover credits and a 100% money-back guarantee
            </p>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Most customers recover the full cost within the first week by capturing just 1-2 opportunities they would have otherwise lost
            </p>
          </div>
          <Suspense fallback={<LoadingFallback />}>
            <PricingPlans onPlanSelect={(planId, billingCycle) => {
            navigate("/dashboard");
          }} />
          </Suspense>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-5 bg-muted/30" aria-labelledby="faq">
        <div className="container mx-auto px-4">
          <div className="text-center mb-4">
            <h2 id="faq" className="text-3xl lg:text-4xl font-bold mb-2">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need to know about iQ Texting
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-background rounded-lg border px-6">
                <AccordionTrigger className="text-xl font-semibold text-primary hover:no-underline">
                  How does iQ Texting work with my existing website?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pt-2">
                  iQ Texting integrates seamlessly with any website through a simple code snippet. Once installed, a chat widget appears on your site where visitors can enter their name, phone number, and message. When they submit, your team members receive the message as a text on their phones instantly. You can reply directly from your phone, and your response appears in the chat widget on the visitor&apos;s screen - creating a seamless two-way conversation without either party leaving their preferred platform.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-background rounded-lg border px-6">
                <AccordionTrigger className="text-xl font-semibold text-primary hover:no-underline">
                  Can multiple team members receive messages from the same visitor?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pt-2">
                  Yes! When a visitor submits a message through your website, you can configure up to 7 team members to receive the notification simultaneously via text message. The first person to respond takes ownership of that conversation, and all subsequent messages are routed only to that agent. This ensures quick response times while preventing duplicate or conflicting replies. You can manage your team structure and notification settings directly from the dashboard, assigning different roles and permissions as needed.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-background rounded-lg border px-6">
                <AccordionTrigger className="text-xl font-semibold text-primary hover:no-underline">
                  What happens when my team is unavailable or outside business hours?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pt-2">
                  iQ Texting includes an intelligent AI assistant that automatically handles conversations during your off-hours. You can set your business hours in the dashboard, and when messages arrive outside those times, the AI responds based on your custom knowledge base and training. The AI can answer common questions, collect customer information, and even schedule appointments. When your team returns, you can review all AI conversations and follow up as needed. You maintain full control over what the AI can and cannot do, ensuring brand consistency and quality.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-background rounded-lg border px-6">
                <AccordionTrigger className="text-xl font-semibold text-primary hover:no-underline">
                  How are messages charged? What happens if I exceed my plan limit?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pt-2">
                  Each plan includes a specific number of messages per month (both sent and received count toward this limit). Your current usage is displayed prominently in your dashboard so you always know where you stand. If you exceed your monthly limit, overage messages are automatically bundled at $1.50 per 20-message bundle - ensuring you never miss a customer conversation. You can upgrade to a higher plan at any time if you consistently use more messages, or add specialized add-ons like extra phone numbers or advanced analytics to customize your experience.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-background rounded-lg border px-6">
                <AccordionTrigger className="text-xl font-semibold text-primary hover:no-underline">
                  Is my customer data secure? How do you handle privacy?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pt-2">
                  Security and privacy are our top priorities. All customer data is encrypted both in transit and at rest. Each organization&apos;s data is completely isolated in our multi-tenant architecture, meaning your conversations, contacts, and business information are never accessible to other organizations. We comply with industry-standard security practices and regulations. Team members can only access data for their assigned organization, and you control who on your team can view or manage different aspects of your account through granular permission settings. We never sell or share your customer data with third parties.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-background rounded-lg border px-6">
                <AccordionTrigger className="text-xl font-semibold text-primary hover:no-underline">
                  Can I integrate iQ Texting with my existing CRM or business tools?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pt-2">
                  Yes! iQ Texting includes a built-in CRM for managing contacts, tracking conversation history, and storing customer information. So if you do not have a CRM, you now do. 92.7% of businesses in the US do not have a way to keep contact information! For businesses that already use external CRM systems, we offer integration capabilities on our Professional and Business plans. You can sync contacts, activities, and conversation data with popular CRM platforms. The system also supports custom integrations through our API (available on Business plan), allowing you to connect with virtually any tool in your tech stack. All integrations maintain the same security and privacy standards as our core platform.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7" className="bg-background rounded-lg border px-6">
                <AccordionTrigger className="text-xl font-semibold text-primary hover:no-underline">
                  What kind of analytics and reporting do you provide?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pt-2">
                  The dashboard provides comprehensive real-time analytics including active conversations, response times, message volume, and agent performance metrics. You can track daily, weekly, and monthly trends to understand your customer engagement patterns. Advanced analytics features (available on higher-tier plans) include sentiment analysis, AI performance tracking, conversion metrics, and custom reporting. All data can be filtered by date range, agent, conversation status, and more. These insights help you optimize your team&apos;s performance and identify opportunities to improve customer satisfaction.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8" className="bg-background rounded-lg border px-6">
                <AccordionTrigger className="text-xl font-semibold text-primary hover:no-underline">
                  How quickly can I get started, and is there training available?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pt-2">
                  You can be up and running in minutes! After signing up, you&apos;ll go through a simple onboarding process to set up your organization, add team members, and configure your business hours. The widget code can be added to your website with a single line of JavaScript - no technical expertise required. We provide detailed documentation, video tutorials, and best practices guides to help you maximize your success. For Business plan customers, we offer dedicated onboarding support and training sessions. Our support team is available via email and chat to answer any questions as you get started.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-roi" className="bg-background rounded-lg border px-6">
                <AccordionTrigger className="text-xl font-semibold text-primary hover:no-underline">
                  How quickly will iQ Texting pay for itself in MY industry?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pt-2">
                  <p className="mb-4">
                    Most businesses see ROI within the <strong>FIRST WEEK</strong> - but let&apos;s look at specific examples:
                  </p>
                  
                  <div className="space-y-4">
                    {/* Restaurant Example */}
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <p className="font-bold text-orange-900 mb-2">🍽️ RESTAURANTS (Starter Plan - $29.97/month):</p>
                      <p className="text-sm">If you miss just 4 reservations/month at $100 average = $400 lost</p>
                      <p className="text-sm">iQ Texting costs $29.97/month</p>
                      <p className="text-sm font-bold text-green-700">ROI: Recover those 4 and you&apos;re up $370/month profit</p>
                      <p className="text-xs text-gray-600">Payback period: 1 week</p>
                    </div>

                    {/* HVAC Example */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="font-bold text-blue-900 mb-2">🔧 HVAC/PLUMBING (Professional Plan - $49.97/month):</p>
                      <p className="text-sm">If you miss 2 service calls/month at $850 average = $1,700 lost</p>
                      <p className="text-sm">iQ Texting costs $49.97/month</p>
                      <p className="text-sm font-bold text-green-700">ROI: Recover those 2 and you&apos;re up $1,650/month profit</p>
                      <p className="text-xs text-gray-600">Payback period: 1 day</p>
                    </div>

                    {/* Roofing Example */}
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <p className="font-bold text-purple-900 mb-2">🏗️ ROOFING (Business Plan - $79.97/month):</p>
                      <p className="text-sm">If you miss 1 estimate/month that converts to a $12,000 job</p>
                      <p className="text-sm">iQ Texting costs $79.97/month</p>
                      <p className="text-sm font-bold text-green-700">ROI: Land that ONE job = 150 months (12.5 YEARS) paid for</p>
                      <p className="text-xs text-gray-600">Payback period: The first phone call you answer</p>
                    </div>
                  </div>

                  <p className="mt-4 font-bold">
                    The real question: Can you afford to keep losing these opportunities?
                  </p>
                  
                  <p className="mt-4 text-sm">
                    <a href="#industry-calculators" className="text-primary hover:underline">
                      → Use our industry calculator to see YOUR exact ROI
                    </a>
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-3" aria-labelledby="cta">
        <div className="container mx-auto px-4">
          <aside className="p-4 text-center border-0 shadow-[var(--shadow-hover)] bg-primary relative overflow-hidden rounded-lg">
            <div className="absolute inset-0 bg-gradient-to-br from-black/10 via-transparent to-black/5" />
            <div className="relative">
              <h2 id="cta" className="text-3xl lg:text-4xl font-bold text-white mb-2 drop-shadow-sm">
                Ready to Transform Your Customer Engagement?
              </h2>
              <p className="text-lg text-white mb-2.5 max-w-2xl mx-auto drop-shadow-sm">
                Start capturing and converting more leads today with intelligent text messaging
              </p>
              <Button variant="secondary" size="lg" className="gap-2 bg-white text-primary hover:bg-white/90" onClick={() => navigate("/dashboard")}>
                Get Started Now
                <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </Button>
            </div>
          </aside>
        </div>
      </section>
      </main>

      <TrustBadges />
      
      {/* Case Studies Section - Lazy Loaded */}
      <Suspense fallback={<LoadingFallback />}>
        <CaseStudiesSection />
      </Suspense>

      {/* Testimonials Section - Lazy Loaded */}
      <Suspense fallback={<LoadingFallback />}>
        <TestimonialsSection />
      </Suspense>

      <ChatWidget />

      {/* Footer */}
      <Footer />
    </div>;
};
export default Index;